import React from "react";
import { Alert, Stack } from "@mui/material";
import { DeviceDetailSectionName, DeviceSnapshotDetailsResponse, DeviceSnapshotSample, DeviceSnapshotMetrics } from "../apis/devicesApi";

export function sectionHasData(sample: DeviceSnapshotSample | null | undefined, name: DeviceDetailSectionName): boolean {
  return ["ready", "stale"].includes(sample?.sections?.[name]?.state || "");
}
export function sectionCount(sample: DeviceSnapshotSample | null | undefined, name: DeviceDetailSectionName, count: number): number | string {
  return sectionHasData(sample, name) ? count : sample?.sections?.[name]?.state === "unsupported" ? "Not supported" : "Unavailable";
}
/** Datto audit hardware is usable; alert-derived CPU/RAM values are not telemetry. */
export function displayDeviceMetrics(sample: DeviceSnapshotSample | null | undefined): DeviceSnapshotMetrics {
  const metrics = sample?.metrics || {};
  if (sample?.provider !== "datto") return metrics;
  const auditAvailable = sectionHasData(sample, "audit");
  return { ...metrics,
    cpu: auditAvailable ? { ...metrics.cpu, used_percent: null, usage_source: null } : null,
    memory: auditAvailable ? { ...metrics.memory, used_percent: null, free_percent: null, usage_source: null } : null,
    disks: auditAvailable ? metrics.disks : null,
  };
}

function refreshMessage(details: DeviceSnapshotDetailsResponse) {
  const status = details.refresh_status;
  if (status?.state === "failed") return "Automatic refresh failed. Use Refresh to try again.";
  if (status?.state === "retrying") return "Refresh is delayed; we’ll retry automatically.";
  if (details.refreshing && status && Date.now() - status.requested_at_ms >= 120000) return "Refresh is taking longer than expected; we’re still checking.";
  if (status?.state === "running") return "Refreshing device data.";
  if (status?.state === "queued") return "Refresh is queued.";
  return "";
}
export default function DeviceDataNotices({ details }: { details: DeviceSnapshotDetailsResponse }) {
  const unavailable = Object.entries(details.latest_sample?.sections || {}).filter(([, value]) => value && ["stale", "unavailable"].includes(value.state));
  const labels: Record<string, string> = { device: "Device status", audit: "Hardware and disk audit", software: "Applications", alerts: "Alerts" };
  return <Stack spacing={1}>
    {(details.stale || details.refreshing || details.refresh_status?.state === "failed") && <Alert severity={details.refresh_status?.error ? "warning" : "info"}>
      Showing cached device data. {refreshMessage(details)}{details.refresh_status?.error?.message ? ` ${details.refresh_status.error.message}` : ""}
    </Alert>}
    {unavailable.map(([name, value]) => <Alert key={name} severity="warning">
      {labels[name]}: {value!.state === "stale" ? `showing the last successful data from ${new Date(value!.sampled_at_ms!).toLocaleString()}.` : "unavailable."}
      {value!.error?.message ? ` ${value!.error.message}` : ""}
    </Alert>)}
  </Stack>;
}
